
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Search, PiggyBank } from "lucide-react";
import { MainNavigation } from "../navigation/MainNavigation";
import MobileMenu from "../navigation/MobileMenu";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  console.log("🔥 Header component rendered, isMenuOpen:", isMenuOpen);
  
  // Improved path detection - check article paths explicitly
  const isArticlePage = location.pathname.startsWith('/artikkelit');
  const isEnglish = location.pathname.startsWith('/en') && !isArticlePage;
  const isSwedish = location.pathname.startsWith('/sv') && !isArticlePage;
  const isArabic = location.pathname.startsWith('/ar') && !isArticlePage;
  const isHebrew = location.pathname.startsWith('/he') && !isArticlePage;

  // Close mobile menu when route changes
  useEffect(() => {
    if (isMenuOpen) {
      console.log("🔥 Route changed, closing menu");
      setIsMenuOpen(false);
    }
  }, [location.pathname]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // If on article page, always go to Finnish homepage
    if (isArticlePage) {
      navigate('/');
    } else if (isEnglish) {
      navigate('/en');
    } else if (isSwedish) {
      navigate('/sv');
    } else if (isArabic) {
      navigate('/ar');
    } else if (isHebrew) {
      navigate('/he');
    } else {
      navigate('/');
    }
  };

  const toggleMobileMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("🔥 HAMBURGER CLICKED! Current isMenuOpen:", isMenuOpen);
    const newState = !isMenuOpen;
    console.log("🔥 Setting isMenuOpen to:", newState);
    setIsMenuOpen(newState);
    
    // Check if state actually changed
    setTimeout(() => {
      console.log("🔥 After setState, isMenuOpen should be:", newState);
    }, 10);
  };

  console.log("🔥 About to render, isMenuOpen is:", isMenuOpen);

  return (
    <>
      <header className="bg-gradient-to-b from-blue-50 to-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <a 
                href="#" 
                onClick={handleLogoClick}
                className="flex items-center gap-2 text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors"
                aria-label="Lainasampo.fi etusivu"
              >
                <div className="bg-blue-600 bg-opacity-10 p-2 rounded-full">
                  <PiggyBank className="h-8 w-8 text-blue-500" />
                </div>
                <span className="font-serif tracking-wide bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">Lainasampo.fi</span>
              </a>
            </div>
            
            <div className="hidden md:flex">
              <MainNavigation 
                isEnglish={isEnglish} 
                isSwedish={isSwedish} 
                isArabic={isArabic} 
                isHebrew={isHebrew} 
                isArticlePage={isArticlePage}
              />
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              {/* Mobile Search */}
              <div className="md:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Search className="h-5 w-5 text-blue-600" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="top" className="h-[50vh]">
                    <div className="pt-6 pb-10">
                      <h3 className="text-lg font-medium mb-4">
                        {isEnglish ? "Search loans and products" : 
                         isSwedish ? "Sök lån och produkter" : 
                         isArabic ? "البحث عن القروض والمنتجات" :
                         isHebrew ? "חיפוש הלוואות ומוצרים" :
                         "Hae lainoista ja tuotteista"}
                      </h3>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input 
                          type="text" 
                          placeholder={
                            isEnglish ? "Type search term..." : 
                            isSwedish ? "Skriv sökord..." : 
                            isArabic ? "اكتب مصطلح البحث..." :
                            isHebrew ? "הקלד מונח חיפוש..." :
                            "Kirjoita hakusana..."
                          }
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-500 mb-2">
                          {isEnglish ? "Most popular searches:" : 
                           isSwedish ? "Populäraste sökningarna:" : 
                           isArabic ? "عمليات البحث الأكثر شيوعًا:" :
                           isHebrew ? "החיפושים הפופולריים ביותר:" :
                           "Suosituimmat haut:"}
                        </h4>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {isEnglish ? (
                            <>
                              <Link to="/en/apply" className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                Mortgage
                              </Link>
                              <Link to="/en/apply" className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                Car loans
                              </Link>
                            </>
                          ) : isSwedish ? (
                            <>
                              <Link to="/sv/apply" className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                Bolån
                              </Link>
                              <Link to="/sv/apply" className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                Billån
                              </Link>
                            </>
                          ) : isArabic || isHebrew ? (
                            <>
                              <Link to={isArabic ? "/ar/apply" : "/he/apply"} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                {isArabic ? "القروض العقارية" : "משכנתאות"}
                              </Link>
                              <Link to={isArabic ? "/ar/apply" : "/he/apply"} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                {isArabic ? "قروض السيارات" : "הלוואות רכב"}
                              </Link>
                            </>
                          ) : (
                            <>
                              <Link to="/kilpailuta" className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                Asuntolaina
                              </Link>
                              <Link to="/kilpailuta" className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100">
                                Autolaina
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>

              {/* Language Selector - ONLY using standard short codes */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Select Language</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/" className="w-full cursor-pointer">
                      Suomi
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/en" className="w-full cursor-pointer">
                      English
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/sv" className="w-full cursor-pointer">
                      Svenska
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/ar" className="w-full cursor-pointer">
                      العربية
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/he" className="w-full cursor-pointer">
                      עברית
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Mobile Menu Button - Closer to language selector */}
              <div className="md:hidden">
                <button
                  type="button"
                  onClick={toggleMobileMenu}
                  className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-blue-200 bg-white shadow-sm"
                  aria-label="Avaa mobiilimenu"
                  aria-expanded={isMenuOpen}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    {isMenuOpen ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Navigation Menu */}
      <div className="md:hidden">
        <MobileMenu 
          isMenuOpen={isMenuOpen} 
          setIsMenuOpen={setIsMenuOpen} 
          isEnglish={isEnglish}
          isSwedish={isSwedish} 
          isArabic={isArabic}
          isHebrew={isHebrew}
          isArticlePage={isArticlePage}
        />
      </div>
    </>
  );
};

export default Header;
